import {
    BathroomOptions,
    BedroomOptions,
    CarParkOptions,
    LotSizeOptions,
    PropertyStyles,
    PropertyTypes,
    PurchaseTimeframes,
    RenovationPreferences,
    SettlementTerms,
    WishlistSchema,
    YesNoUnsure,
} from '@buyerflo/core/schemas/purchaser'
import { zodResolver } from '@hookform/resolvers/zod'
import { Check } from '@tamagui/lucide-icons-2'
import Button from 'components/button'
import { Checkbox, SizableText, View, XStack, YStack } from 'tamagui'
import { Label } from 'components/label'
import Select, { Option } from 'components/select'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { SlotInput, SlotTextArea } from 'components/input'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTRPC } from 'utils/trpc'

export default () => {
    const { orgId } = useParams<{ orgId: string }>()
    const [searchParams, setSearchParams] = useSearchParams()
    const navigate = useNavigate()
    const trpc = useTRPC()
    const queryClient = useQueryClient()
    const [submitted, setSubmitted] = useState(searchParams.get('done') === 'true')
    const [error, setError] = useState<string | null>(null)
    const isOnboarding = searchParams.get('onboarding') === 'true'

    useEffect(() => {
        if (!isOnboarding) document.title = 'Wishlist'
    }, [isOnboarding])

    const userEmail = searchParams.get('email')
    const wishlistTestEmail = useMemo(() => {
        if (!userEmail) return 'test@example.com'
        const [local, domain] = userEmail.split('@')
        return `${local}+wishlisttest@${domain}`
    }, [userEmail])

    const defaultValues = useMemo((): Partial<WishlistSchema> => {
        const base: Partial<WishlistSchema> = {
            preferredCommunication: [],
            propertyTypes: [],
            propertyStyles: [],
        }
        if (!isOnboarding) return base
        return {
            ...base,
            firstName: 'Test',
            lastName: 'Client',
            email: wishlistTestEmail,
            postalAddress: '123 Test Street, Brisbane QLD 4000',
            purchaseEntity: 'Test Client',
            budgetRange: '$2M',
            settlementTerms: '60 Days',
            purchaseTimeframe: 'Within 3-6 Months',
            contactPerson: 'Test Client',
            preferredCommunication: ['Email', 'Phone Call'],
            propertyTypes: ['House'],
            propertyStyles: ['Modern Contemporary'],
            renovationPreference: 'Yes but only minor cosmetic renovation',
            wantPool: 'Yes',
            bedrooms: '3-4',
            bathrooms: '2-3',
            carParks: 'Double Garage',
            subjectToFIRB: 'No',
            targetSuburbs: 'Paddington, Red Hill, Bardon',
            detailedDescription: 'A modern 3-4 bedroom home with open-plan living, a nice backyard, and close to schools and public transport. Preference for a quiet street with established trees.',
        }
    }, [isOnboarding, wishlistTestEmail])

    const { handleSubmit, control, formState } = useForm<WishlistSchema>({
        resolver: zodResolver(WishlistSchema),
        defaultValues,
    })

    const submitMutation = useMutation(trpc.publicWishlist.submit.mutationOptions())

    const onSubmit = async (data: WishlistSchema) => {
        if (!orgId) {
            setError('Organization ID is required')
            return
        }

        setError(null)

        try {
            const result = await submitMutation.mutateAsync({
                orgId,
                wishlist: data,
            })
            setSubmitted(true)
            if (!isOnboarding) {
                setSearchParams(prev => { prev.set('done', 'true'); return prev }, { replace: true })
            }
            if (isOnboarding && result.purchaserId) {
                // The clients list reads DynamoDB directly, so the new client is
                // there as soon as the submit resolves — just drop the stale caches.
                await queryClient.invalidateQueries({ queryKey: [['purchaser']] })
                await queryClient.invalidateQueries({ queryKey: [['onboarding']] })
                navigate(`/${orgId}/clients?from=wishlist&purchaserId=${result.purchaserId}`)
            } else if (isOnboarding) {
                navigate(`/${orgId}/clients?from=wishlist`)
            }
        } catch (err: any) {
            setError(err.message || 'Failed to submit wishlist. Please try again.')
        }
    }

    if (submitted) {
        return (
            <View
                flexDirection="row"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                padding="$2"
            >
                <View
                    borderWidth={1}
                    borderColor="$borderColor"
                    borderRadius="$3"
                    maxWidth={600}
                    width="100%"
                    padding="$4"
                    alignItems="center"
                    backgroundColor="$backgroundStrong"
                >
                    <SizableText
                        fontSize={24}
                        fontWeight="700"
                        letterSpacing={-0.3}
                        marginBottom="$2"
                    >
                        Thank You!
                    </SizableText>
                    <SizableText size="$5">
                        Your property wishlist has been submitted successfully. We'll be in touch soon!
                    </SizableText>
                </View>
            </View>
        )
    }

    return (
        <View
            flexDirection="row"
            justifyContent="center"
            minHeight="100vh"
            padding="$2"
            backgroundColor="$warmGray2"
        >
            <View
                borderWidth={1}
                borderColor="$borderColor"
                borderRadius="$3"
                maxWidth={800}
                width="100%"
                padding="$5"
                $sm={{ padding: '$3' }}
                marginVertical="$4"
                backgroundColor="$backgroundStrong"
            >
                {isOnboarding && (
                    <YStack
                        padding="$3"
                        backgroundColor="$blue2"
                        borderRadius="$3"
                        borderWidth={1}
                        borderColor="$blue6"
                        marginBottom="$3"
                    >
                        <SizableText size="$4" fontWeight="600" color="$blue11">
                            Onboarding Preview
                        </SizableText>
                        <SizableText size="$3" color="$blue11">
                            We have pre-filled some dummy info for you. Scroll down and press submit to see how a new client gets created.
                        </SizableText>
                    </YStack>
                )}

                <YStack marginBottom="$4" alignItems="center">
                    <SizableText
                        size="$1"
                        textTransform="uppercase"
                        color="$gray10"
                    >
                        New Home Buyer
                    </SizableText>
                    <SizableText
                        fontSize={28}
                        fontWeight="700"
                        letterSpacing={-0.3}
                        marginBottom="$1"
                    >
                        Property Wish List
                    </SizableText>
                    <SizableText
                        size="$5"
                        fontWeight="600"
                        textTransform="uppercase"
                    >
                        Complete the questionnaire below
                    </SizableText>
                    <SizableText
                        size="$3"
                        marginTop="$2"
                        textAlign="center"
                    >
                        This is your space to tell us everything you want and need in your new home. From the
                        criteria you specify, we can prepare your personalised Buyer Brief which will form the
                        blueprint for our property search.
                    </SizableText>
                </YStack>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <YStack gap="$4">
                        {/* Name Section */}
                        <XStack
                            gap="$2"
                            $sm={{ flexDirection: 'column' }}
                        >
                            <Controller
                                name="firstName"
                                control={control}
                                render={({ field, fieldState, formState }) => (
                                    <SlotInput
                                        label="First Name (required)"
                                        field={field}
                                        fieldState={fieldState}
                                        formState={formState}
                                    />
                                )}
                            />
                            <Controller
                                name="lastName"
                                control={control}
                                render={({ field, fieldState, formState }) => (
                                    <SlotInput
                                        label="Last Name (required)"
                                        field={field}
                                        fieldState={fieldState}
                                        formState={formState}
                                    />
                                )}
                            />
                        </XStack>

                        {/* Email */}
                        <Controller
                            name="email"
                            control={control}
                            render={({ field, fieldState, formState }) => (
                                <SlotInput
                                    label="Email (required)"
                                    type="email"
                                    field={field}
                                    fieldState={fieldState}
                                    formState={formState}
                                />
                            )}
                        />

                        {/* Sign Up For Updates */}
                        <YStack>
                            <Controller
                                name="signUpForUpdates"
                                control={control}
                                render={({ field }) => (
                                    <XStack alignItems="center" gap="$2">
                                        <Checkbox
                                            checked={field.value}
                                            onCheckedChange={checked => field.onChange(checked)}
                                            id="signUpForUpdates"
                                        >
                                            <Checkbox.Indicator>
                                                <Check />
                                            </Checkbox.Indicator>
                                        </Checkbox>
                                        <Label inline htmlFor="signUpForUpdates">Sign up for news and updates</Label>
                                    </XStack>
                                )}
                            />
                        </YStack>

                        {/* Postal Address */}
                        <Controller
                            name="postalAddress"
                            control={control}
                            render={({ field, fieldState, formState }) => (
                                <SlotInput
                                    label="Postal Address (required)"
                                    field={field}
                                    fieldState={fieldState}
                                    formState={formState}
                                />
                            )}
                        />

                        {/* Purchase Entity */}
                        <Controller
                            name="purchaseEntity"
                            control={control}
                            render={({ field, fieldState, formState }) => (
                                <SlotInput
                                    label="Please provide the PURCHASE ENTITY for this purchase. (required)"
                                    helperText="(The Purchase Entity is the legal owner of the property you intend to buy. Eg. if buying the property in your personal name/s the purchase entity will be your FULL LEGAL NAMES including any middle names. If buying in a company OR a trust, please provide the full entity name including ABN and ACN.)"
                                    field={field}
                                    fieldState={fieldState}
                                    formState={formState}
                                />
                            )}
                        />

                        {/* FIRB */}
                        <YStack>
                            <Label>
                                Is this purchase subject to Foreign Investment Review (FIRB)?
                            </Label>
                            <SizableText size="$2" color="$gray10">
                                FIRB typically applies to non-residents and foreign buyers.
                            </SizableText>
                            <Controller
                                name="subjectToFIRB"
                                control={control}
                                render={({ field }) => (
                                    <XStack gap="$2">
                                        {YesNoUnsure.map(option => (
                                            <XStack key={option} alignItems="center" gap="$1">
                                                <Checkbox
                                                    checked={field.value === option}
                                                    onCheckedChange={() => field.onChange(option)}
                                                    id={`firb-${option}`}
                                                >
                                                    <Checkbox.Indicator>
                                                        <Check />
                                                    </Checkbox.Indicator>
                                                </Checkbox>
                                                <Label inline htmlFor={`firb-${option}`}>{option}</Label>
                                            </XStack>
                                        ))}
                                    </XStack>
                                )}
                            />
                        </YStack>

                        {/* Budget Range */}
                        <Controller
                            name="budgetRange"
                            control={control}
                            render={({ field, fieldState, formState }) => (
                                <SlotInput
                                    label="Your proposed BUDGET range. (required)"
                                    helperText="(Eg. $2.5M to $3M.)"
                                    field={field}
                                    fieldState={fieldState}
                                    formState={formState}
                                />
                            )}
                        />

                        {/* Settlement Terms */}
                        <YStack>
                            <Label>Your preferred SETTLEMENT terms.</Label>
                            <Controller
                                name="settlementTerms"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        placeholder="Select the timeframe you wish to settle:"
                                        value={field.value}
                                        onChange={(_, value) => field.onChange(value)}
                                    >
                                        {SettlementTerms.map(term => (
                                            <Option
                                                key={term}
                                                value={term}
                                            >
                                                {term}
                                            </Option>
                                        ))}
                                    </Select>
                                )}
                            />
                        </YStack>

                        {/* Purchase Timeframe */}
                        <YStack>
                            <Label>Your ideal PURCHASE TIMEFRAME.</Label>
                            <SizableText size="$2" color="$gray10">
                                (Eg. when do you wish to have the purchase completed by?)
                            </SizableText>
                            <Controller
                                name="purchaseTimeframe"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        placeholder="I'd like the purchase to be complete by:"
                                        value={field.value}
                                        onChange={(_, value) => field.onChange(value)}
                                    >
                                        {PurchaseTimeframes.map(timeframe => (
                                            <Option
                                                key={timeframe}
                                                value={timeframe}
                                            >
                                                {timeframe}
                                            </Option>
                                        ))}
                                    </Select>
                                )}
                            />
                        </YStack>

                        {/* Contact Person */}
                        <Controller
                            name="contactPerson"
                            control={control}
                            render={({ field, fieldState, formState }) => (
                                <SlotInput
                                    label="Name of the best CONTACT PERSON for communications."
                                    field={field}
                                    fieldState={fieldState}
                                    formState={formState}
                                />
                            )}
                        />

                        {/* Preferred Communication */}
                        <YStack>
                            <Label>Preferred FORM OF COMMUNICATION.</Label>
                            <SizableText size="$2" color="$gray10">(Select ALL that apply.)</SizableText>
                            <Controller
                                name="preferredCommunication"
                                control={control}
                                render={({ field }) => (
                                    <XStack
                                        gap="$2"
                                        flexWrap="wrap"
                                    >
                                        {['Text', 'Phone Call', 'WhatsApp Chat', 'Email'].map(option => (
                                            <XStack key={option} alignItems="center" gap="$1">
                                                <Checkbox
                                                    checked={field.value?.includes(option as any)}
                                                    onCheckedChange={checked => {
                                                        const current = field.value || []
                                                        if (checked) {
                                                            field.onChange([...current, option])
                                                        } else {
                                                            field.onChange(current.filter(v => v !== option))
                                                        }
                                                    }}
                                                    id={`comm-${option}`}
                                                >
                                                    <Checkbox.Indicator>
                                                        <Check />
                                                    </Checkbox.Indicator>
                                                </Checkbox>
                                                <Label inline htmlFor={`comm-${option}`}>{option}</Label>
                                            </XStack>
                                        ))}
                                    </XStack>
                                )}
                            />
                        </YStack>

                        {/* Best Contact Times */}
                        <Controller
                            name="bestContactTimes"
                            control={control}
                            render={({ field, fieldState, formState }) => (
                                <SlotInput
                                    label="Best CONTACT TIMES for communications."
                                    helperText="(Eg. before 9am or after 5pm.)"
                                    field={field}
                                    fieldState={fieldState}
                                    formState={formState}
                                />
                            )}
                        />

                        {/* Property Types */}
                        <YStack>
                            <Label>Type of PROPERTY I'm buying is:</Label>
                            <SizableText size="$2" color="$gray10">(Select ALL that apply.)</SizableText>
                            <Controller
                                name="propertyTypes"
                                control={control}
                                render={({ field }) => (
                                    <XStack
                                        gap="$2"
                                        flexWrap="wrap"
                                    >
                                        {PropertyTypes.map(type => (
                                            <XStack key={type} alignItems="center" gap="$1">
                                                <Checkbox
                                                    checked={field.value?.includes(type)}
                                                    onCheckedChange={checked => {
                                                        const current = field.value || []
                                                        if (checked) {
                                                            field.onChange([...current, type])
                                                        } else {
                                                            field.onChange(current.filter(v => v !== type))
                                                        }
                                                    }}
                                                    id={`proptype-${type}`}
                                                >
                                                    <Checkbox.Indicator>
                                                        <Check />
                                                    </Checkbox.Indicator>
                                                </Checkbox>
                                                <Label inline htmlFor={`proptype-${type}`}>{type}</Label>
                                            </XStack>
                                        ))}
                                    </XStack>
                                )}
                            />
                        </YStack>

                        {/* Property Styles */}
                        <YStack>
                            <Label>Your preferred PROPERTY STYLE(S).</Label>
                            <SizableText size="$2" color="$gray10">(Select ALL that apply.)</SizableText>
                            <Controller
                                name="propertyStyles"
                                control={control}
                                render={({ field }) => (
                                    <XStack
                                        gap="$2"
                                        flexWrap="wrap"
                                    >
                                        {PropertyStyles.map(style => (
                                            <XStack key={style} alignItems="center" gap="$1">
                                                <Checkbox
                                                    checked={field.value?.includes(style)}
                                                    onCheckedChange={checked => {
                                                        const current = field.value || []
                                                        if (checked) {
                                                            field.onChange([...current, style])
                                                        } else {
                                                            field.onChange(current.filter(v => v !== style))
                                                        }
                                                    }}
                                                    id={`propstyle-${style}`}
                                                >
                                                    <Checkbox.Indicator>
                                                        <Check />
                                                    </Checkbox.Indicator>
                                                </Checkbox>
                                                <Label inline htmlFor={`propstyle-${style}`}>{style}</Label>
                                            </XStack>
                                        ))}
                                    </XStack>
                                )}
                            />
                        </YStack>

                        {/* Renovations */}
                        <YStack>
                            <Label>Are you open to Renovations?</Label>
                            <Controller
                                name="renovationPreference"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        placeholder="Select your preference:"
                                        value={field.value}
                                        onChange={(_, value) => field.onChange(value)}
                                    >
                                        {RenovationPreferences.map(pref => (
                                            <Option
                                                key={pref}
                                                value={pref}
                                            >
                                                {pref}
                                            </Option>
                                        ))}
                                    </Select>
                                )}
                            />
                        </YStack>

                        {/* Pool */}
                        <YStack>
                            <Label>Do you want a Pool?</Label>
                            <Controller
                                name="wantPool"
                                control={control}
                                render={({ field }) => (
                                    <XStack gap="$2">
                                        {YesNoUnsure.map(option => (
                                            <XStack key={option} alignItems="center" gap="$1">
                                                <Checkbox
                                                    checked={field.value === option}
                                                    onCheckedChange={() => field.onChange(option)}
                                                    id={`pool-${option}`}
                                                >
                                                    <Checkbox.Indicator>
                                                        <Check />
                                                    </Checkbox.Indicator>
                                                </Checkbox>
                                                <Label inline htmlFor={`pool-${option}`}>{option}</Label>
                                            </XStack>
                                        ))}
                                    </XStack>
                                )}
                            />
                        </YStack>

                        {/* Bedrooms */}
                        <YStack>
                            <Label>How many BEDROOMS do you require?</Label>
                            <Controller
                                name="bedrooms"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        placeholder="Select number of bedrooms:"
                                        value={field.value}
                                        onChange={(_, value) => field.onChange(value)}
                                    >
                                        {BedroomOptions.map(option => (
                                            <Option
                                                key={option}
                                                value={option}
                                            >
                                                {option}
                                            </Option>
                                        ))}
                                    </Select>
                                )}
                            />
                        </YStack>

                        {/* Bathrooms */}
                        <YStack>
                            <Label>How many BATHROOMS do you require?</Label>
                            <Controller
                                name="bathrooms"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        placeholder="Select number of bathrooms:"
                                        value={field.value}
                                        onChange={(_, value) => field.onChange(value)}
                                    >
                                        {BathroomOptions.map(option => (
                                            <Option
                                                key={option}
                                                value={option}
                                            >
                                                {option}
                                            </Option>
                                        ))}
                                    </Select>
                                )}
                            />
                        </YStack>

                        {/* Car Parks */}
                        <YStack>
                            <Label>How many CAR PARKS do you require?</Label>
                            <Controller
                                name="carParks"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        placeholder="Select number of parking bays:"
                                        value={field.value}
                                        onChange={(_, value) => field.onChange(value)}
                                    >
                                        {CarParkOptions.map(option => (
                                            <Option
                                                key={option}
                                                value={option}
                                            >
                                                {option}
                                            </Option>
                                        ))}
                                    </Select>
                                )}
                            />
                        </YStack>

                        {/* Schools */}
                        <Controller
                            name="schoolsImportant"
                            control={control}
                            render={({ field, fieldState, formState }) => (
                                <SlotInput
                                    label="Are SCHOOLS important to you?"
                                    helperText="(Eg. do you need to be located in a particular school catchment area?)"
                                    field={field}
                                    fieldState={fieldState}
                                    formState={formState}
                                />
                            )}
                        />

                        {/* Detailed Description */}
                        <Controller
                            name="detailedDescription"
                            control={control}
                            render={({ field, formState }) => (
                                <SlotTextArea
                                    label="Provide a detailed DESCRIPTION of your ideal property. (required)"
                                    helperText="(This is a space to share your specific property requirements and location preferences. Include things like your preferred aspect, specific interior styles, living areas, climate control, entertaining areas, storage spaces, feng shui requirements, whether you'd like to be close to cafes, schools or public transport.)"
                                    minRows={4}
                                    field={field}
                                    formState={formState}
                                />
                            )}
                        />

                        {/* Target Suburbs */}
                        <Controller
                            name="targetSuburbs"
                            control={control}
                            render={({ field, formState }) => (
                                <SlotTextArea
                                    label="Enter your TARGET SUBURBS."
                                    helperText="(Include ALL areas you are considering.)"
                                    minRows={2}
                                    field={field}
                                    formState={formState}
                                />
                            )}
                        />

                        {/* Deal Breakers */}
                        <Controller
                            name="dealBreakers"
                            control={control}
                            render={({ field, formState }) => (
                                <SlotTextArea
                                    label="Enter your DEAL BREAKERS."
                                    helperText="(Eg. not on a train line, busy road or in a flood zone.)"
                                    minRows={2}
                                    field={field}
                                    formState={formState}
                                />
                            )}
                        />

                        {/* Lot Size */}
                        <YStack>
                            <Label>Preferred LOT / Land Size.</Label>
                            <Controller
                                name="preferredLotSize"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        placeholder="In m2:"
                                        value={field.value}
                                        onChange={(_, value) => field.onChange(value)}
                                    >
                                        {LotSizeOptions.map(option => (
                                            <Option
                                                key={option}
                                                value={option}
                                            >
                                                {option}
                                            </Option>
                                        ))}
                                    </Select>
                                )}
                            />
                        </YStack>

                        {/* Specific Properties */}
                        <Controller
                            name="specificProperties"
                            control={control}
                            render={({ field, formState }) => (
                                <SlotTextArea
                                    label="List any SPECIFIC PROPERTIES you wish to target in our search."
                                    helperText="Share addresses HERE:"
                                    minRows={2}
                                    field={field}
                                    formState={formState}
                                />
                            )}
                        />

                        {/* Current Properties */}
                        <Controller
                            name="currentListingsInterest"
                            control={control}
                            render={({ field, formState }) => (
                                <SlotTextArea
                                    label="Share examples of CURRENT PROPERTIES you are interested in."
                                    helperText="Enter property links HERE:"
                                    minRows={2}
                                    field={field}
                                    formState={formState}
                                />
                            )}
                        />

                        {/* Solicitor Details */}
                        <Controller
                            name="solicitorDetails"
                            control={control}
                            render={({ field, formState }) => (
                                <SlotTextArea
                                    label="Name and contact details of your nominated SOLICITOR."
                                    helperText="(A referral can be made if required.)"
                                    minRows={2}
                                    field={field}
                                    formState={formState}
                                />
                            )}
                        />

                        {/* Banker/Broker Details */}
                        <Controller
                            name="bankerBrokerDetails"
                            control={control}
                            render={({ field, formState }) => (
                                <SlotTextArea
                                    label="Name and contact details of your BANKER / MORTGAGE BROKER."
                                    helperText="(A referral can be made if required.)"
                                    minRows={2}
                                    field={field}
                                    formState={formState}
                                />
                            )}
                        />

                        {/* Note about finance upload */}
                        <SizableText size="$2" color="$gray10">
                            Note: For uploading finance pre-approval or proof of funds, please contact us
                            directly after submission.
                        </SizableText>

                        {error && (
                            <SizableText
                                color="$red10"
                                size="$3"
                            >
                                {error}
                            </SizableText>
                        )}

                        <Button
                            // @ts-ignore
                            tag="button"
                            type="submit"
                            size="$5"
                            theme="primary"
                            loading={submitMutation.isPending}
                            marginTop="$2"
                            animation="quick"
                            hoverStyle={{ backgroundColor: '$backgroundHover' }}
                            pressStyle={{ backgroundColor: '$backgroundPress', opacity: 0.85 }}
                        >
                            SEND WISH LIST
                        </Button>
                    </YStack>
                </form>
            </View>
        </View>
    )
}
